import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { isStandalone, isIOS, isSafariMac, hasInstallPrompt, takeInstallPrompt, logPwaInstall, supportsPush, notificationPermission } from "../helpers/pwa_platform"

const OPEN_TRANSITION_DELAY_FRAMES = 2
const CLOSE_TRANSITION_MS = 300 // matches the overlay's 0.3s transition -- the longer of the two
const SKIP_EXIT_PATH = "skip"
const BOTTOM_NAV_ID = "navbar-bottom" // app/views/shared/_navbar.html.erb

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(", ")

/**
 * OnboardingWelcomeModal - onboarding guide modal, shown on first login and
 * reopenable from the progress banner (see OnboardingWelcomeController#reopen).
 *
 * Presentation only: locks background scroll, animates the overlay/dialog
 * open on connect, restores scroll on disconnect, traps focus inside the
 * dialog. The three destination cards (Learn/Try/Explore) always navigate
 * away; skip and Escape instead close the modal in place -- no navigation --
 * by reverse-animating the overlay/dialog and removing the element from the
 * DOM, which triggers this controller's own `disconnect()`.
 *
 * Targets: overlay - backdrop element that receives the open transition class |
 *   dialog - the modal panel that receives focus and the open transition class |
 *   skipButton - the "skip" exit control |
 *   installButton - the "install the app" device-step control |
 *   installHint - hidden message shown when this device has no install path
 *   ("open on your phone" instead) |
 *   installIosInstructions - hidden Share -> Add to Home Screen instructions,
 *   toggled open/closed on iOS |
 *   installMacInstructions - hidden Add to Dock instructions, toggled
 *   open/closed on macOS Safari |
 *   notificationsCard - the "turn on notifications" link, locked (aria-disabled)
 *   on a device that can't receive push |
 *   notificationsInstallFirstHint - hidden message shown when this device must
 *   install the app before push will work |
 *   notificationsBlockedHint - hidden message shown when notifications are
 *   already blocked at the browser level
 * Values: dismissUrl - endpoint the chosen exit path is POSTed to; when absent,
 *   reporting is skipped entirely |
 *   syncProgressUrl - endpoint that returns a Turbo Stream refreshing the
 *   progress banner; POSTed right after a device step completes in this same
 *   tab (install accepted, see #install), so the banner's count -- or the
 *   completion celebration -- is current the moment the modal closes, with no
 *   page reload. When absent, the sync is skipped entirely
 * Actions: select - records the chosen destination-card exit path from the
 *   Stimulus param, reports it to dismissUrlValue, and lets the link's default
 *   navigation proceed |
 *   close - the skip control's click handler: reports the "skip" exit path,
 *   then closes the modal in place (see #closeModal) |
 *   install - the install control's click handler: reports the "install" exit
 *   path for measurement, then drives whichever install affordance suits this
 *   device (native prompt, iOS/macOS instructions, or a hint) -- stays open
 *   for all of those except an accepted native prompt, which completes the
 *   step and closes the modal (see #completeInstall) |
 *   notifications - the notifications card's click handler: on a device locked
 *   at connect time (no push support), cancels navigation and reports nothing;
 *   otherwise reports the "notifications" exit path and lets the link navigate
 *
 * @example
 * <div data-controller="onboarding-welcome-modal" id="onboarding_welcome_modal"
 *      data-onboarding-welcome-modal-dismiss-url-value="/onboarding_welcome/dismiss">
 *   <div data-onboarding-welcome-modal-target="overlay"></div>
 *   <div data-onboarding-welcome-modal-target="dialog" tabindex="-1">
 *     <a href="/learn" data-action="click->onboarding-welcome-modal#select"
 *        data-onboarding-welcome-modal-exit-path-param="learn">Learn</a>
 *     <button type="button" data-action="click->onboarding-welcome-modal#install"
 *        data-onboarding-welcome-modal-target="installButton"
 *        data-onboarding-welcome-modal-exit-path-param="install">Install</button>
 *     <button type="button" data-action="click->onboarding-welcome-modal#close"
 *        data-onboarding-welcome-modal-target="skipButton">Skip</button>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = [
    "overlay",
    "dialog",
    "skipButton",
    "installButton",
    "installHint",
    "installIosInstructions",
    "installMacInstructions",
    "notificationsCard",
    "notificationsInstallFirstHint",
    "notificationsBlockedHint"
  ]
  static values = { dismissUrl: String, syncProgressUrl: String }

  connect() {
    this.previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    this.#suppressBottomNav()

    this.onKeydown = this.#handleKeydown.bind(this)
    document.addEventListener("keydown", this.onKeydown)

    // Without this, Turbo snapshots the page (and its cached body overflow)
    // with the modal still open. Restoring that snapshot on Back would bring
    // the modal back with no server round-trip, and disconnect() would then
    // stamp the cached "hidden" overflow onto whatever page Turbo swaps in.
    this.onBeforeCache = () => {
      document.body.style.overflow = this.previousBodyOverflow || ""
      this.element.remove()
    }
    document.addEventListener("turbo:before-cache", this.onBeforeCache)

    this.#nextFrames(OPEN_TRANSITION_DELAY_FRAMES, () => {
      if (this.hasOverlayTarget) this.overlayTarget.classList.add("onboarding-welcome-modal-overlay--open")
      if (this.hasDialogTarget) this.dialogTarget.classList.add("onboarding-welcome-modal-dialog--open")

      this.#focusDialog()
    })

    this.#setupNotificationsCard()
  }

  disconnect() {
    document.body.style.overflow = this.previousBodyOverflow || ""

    this.#restoreBottomNav()

    if (this.onKeydown) {
      document.removeEventListener("keydown", this.onKeydown)
      this.onKeydown = null
    }

    if (this.onBeforeCache) {
      document.removeEventListener("turbo:before-cache", this.onBeforeCache)
      this.onBeforeCache = null
    }

    if (this.openTransitionFrameId) {
      cancelAnimationFrame(this.openTransitionFrameId)
      this.openTransitionFrameId = null
    }
  }

  /**
   * The bottom nav is a page-level `position: fixed` bar; depending on how the
   * modal is mounted it can end up painting over the dialog. While the modal is
   * open we send it to the background and make it inert, so the modal is the
   * only thing on screen and nothing behind it can be tapped. Restored on close.
   */
  #suppressBottomNav() {
    this.bottomNav = document.getElementById(BOTTOM_NAV_ID)
    if (!this.bottomNav) return

    this.previousNavZIndex = this.bottomNav.style.zIndex
    this.previousNavPointerEvents = this.bottomNav.style.pointerEvents
    this.previousNavInert = this.bottomNav.inert

    this.bottomNav.style.zIndex = "0"
    this.bottomNav.style.pointerEvents = "none"
    this.bottomNav.inert = true
  }

  #restoreBottomNav() {
    if (!this.bottomNav) return

    this.bottomNav.style.zIndex = this.previousNavZIndex || ""
    this.bottomNav.style.pointerEvents = this.previousNavPointerEvents || ""
    this.bottomNav.inert = this.previousNavInert || false
    this.bottomNav = null
  }

  /**
   * Records the chosen destination-card exit path and reports it to the
   * server. Never calls preventDefault -- the link must navigate normally.
   * Not used by the skip control; see #close.
   */
  select(event) {
    this.lastExitPath = event.params.exitPath
    this.#reportExit(this.lastExitPath)
  }

  /**
   * The skip control's click handler. Unlike select, this exit never
   * navigates -- it reports "skip" then closes the modal in place.
   */
  close(event) {
    event.preventDefault()
    this.#closeModal()
  }

  /**
   * The install control's click handler. Always reports the "install" exit
   * path for measurement, then drives whichever install affordance suits
   * this device. Unlike select/close, the modal stays open for every
   * affordance except the native browser prompt -- accepting that one
   * finishes the step right here in the same tab (the OS opens the newly
   * installed PWA separately), so #completeInstall closes it and syncs the
   * banner instead of leaving it open on stale progress until a reload.
   */
  async install(event) {
    event.preventDefault()
    this.#reportExit("install")

    if (isStandalone()) return

    if (hasInstallPrompt()) {
      const prompt = takeInstallPrompt()
      prompt.prompt()
      const { outcome } = await prompt.userChoice
      if (outcome === "accepted") await this.#completeInstall()
      return
    }

    if (isIOS()) {
      if (this.hasInstallIosInstructionsTarget) this.installIosInstructionsTarget.classList.toggle("hidden")
      return
    }

    if (isSafariMac()) {
      if (this.hasInstallMacInstructionsTarget) this.installMacInstructionsTarget.classList.toggle("hidden")
      return
    }

    if (this.hasInstallHintTarget) this.installHintTarget.classList.remove("hidden")
  }

  /**
   * The notifications card's click handler. A card locked at connect time
   * (this device can't receive push -- see #setupNotificationsCard) cancels
   * navigation and reports nothing, since the link goes nowhere useful.
   * Otherwise reports the "notifications" exit path and lets the link's
   * default navigation to the notifications page proceed.
   */
  notifications(event) {
    if (this.notificationsLocked) {
      event.preventDefault()
      return
    }

    this.#reportExit("notifications")
  }

  /**
   * Locks the notifications card (and reveals the install-first hint) on a
   * device that can't receive push at all -- notably iOS Safari before the
   * app is installed. Otherwise, if notifications are already blocked at the
   * browser level, reveals the blocked hint but leaves the card clickable
   * (the notifications page itself explains how to unblock).
   */
  #setupNotificationsCard() {
    if (!this.hasNotificationsCardTarget) return

    if (!supportsPush()) {
      this.notificationsLocked = true
      this.notificationsCardTarget.setAttribute("aria-disabled", "true")
      if (this.hasNotificationsInstallFirstHintTarget) this.notificationsInstallFirstHintTarget.classList.remove("hidden")
    } else if (notificationPermission() === "denied") {
      if (this.hasNotificationsBlockedHintTarget) this.notificationsBlockedHintTarget.classList.remove("hidden")
    }
  }

  /**
   * Records the accepted native install prompt as a real pwa.install activity
   * (logPwaInstall -- distinct from #reportExit's "install" tap, which only
   * measures that the button was pressed), refreshes the progress banner in
   * place, then closes the modal without re-reporting an exit path (this step
   * already completed, it isn't being skipped).
   */
  async #completeInstall() {
    await logPwaInstall("chrome")
    await this.#syncProgress()
    this.#closeModal({ report: false })
  }

  /**
   * Best-effort fetch of the current progress banner via Turbo Stream, applied
   * automatically by request.js (window.Turbo is set globally by the
   * @hotwired/turbo import in application.js). A no-op if the endpoint is
   * absent or the request fails -- the next full page load still self-heals.
   */
  async #syncProgress() {
    if (!this.hasSyncProgressUrlValue) return

    try {
      await post(this.syncProgressUrlValue, { responseKind: "turbo-stream" })
    } catch {
      // Best-effort -- the next full page load reflects the real state anyway
    }
  }

  /**
   * Fires the dismiss report and returns immediately without awaiting it.
   * For select(), the link's navigation must proceed on the same tick --
   * `keepalive: true` is what lets the browser finish this POST after the
   * page starts unloading, so do NOT await this fetch before returning.
   */
  #reportExit(exitPath) {
    if (!exitPath || !this.hasDismissUrlValue) return

    const headers = { "Content-Type": "application/json" }
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken

    fetch(this.dismissUrlValue, {
      method: "POST",
      headers,
      body: JSON.stringify({ exit_path: exitPath }),
      keepalive: true
    }).catch(() => {})
  }

  #handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.#closeModal()
      return
    }

    if (event.key === "Tab") {
      this.#trapFocus(event)
    }
  }

  /**
   * Reverse-animates the overlay/dialog and removes the element from the DOM
   * once the transition finishes, which triggers Stimulus's own disconnect()
   * (restoring body scroll, removing the keydown listener) -- no separate
   * cleanup path needed. Guarded against firing twice (a double Escape-press
   * or a stray double-click during the fade-out) so the exit report is never
   * duplicated.
   *
   * report: false skips the "skip" exit report -- used by #completeInstall,
   * where the step already completed and reported itself; it isn't being
   * skipped.
   */
  #closeModal({ report = true } = {}) {
    if (this.closing) return
    this.closing = true

    if (report) {
      this.lastExitPath = SKIP_EXIT_PATH
      this.#reportExit(SKIP_EXIT_PATH)
    }

    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("onboarding-welcome-modal-overlay--open")
    if (this.hasDialogTarget) this.dialogTarget.classList.remove("onboarding-welcome-modal-dialog--open")

    setTimeout(() => this.element.remove(), CLOSE_TRANSITION_MS)
  }

  #trapFocus(event) {
    const focusable = this.#focusableElements()
    if (focusable.length === 0) {
      event.preventDefault()
      return
    }

    event.preventDefault()

    const currentIndex = focusable.indexOf(document.activeElement)
    const lastIndex = focusable.length - 1
    let nextIndex

    if (event.shiftKey) {
      nextIndex = currentIndex <= 0 ? lastIndex : currentIndex - 1
    } else {
      nextIndex = currentIndex === -1 || currentIndex === lastIndex ? 0 : currentIndex + 1
    }

    focusable[nextIndex].focus()
  }

  #focusableElements() {
    if (!this.hasDialogTarget) return []

    return Array.from(this.dialogTarget.querySelectorAll(FOCUSABLE_SELECTOR))
  }

  #focusDialog() {
    if (this.hasDialogTarget) this.dialogTarget.focus()
  }

  #nextFrames(count, callback) {
    if (count <= 0) {
      this.openTransitionFrameId = null
      callback()
      return
    }

    this.openTransitionFrameId = requestAnimationFrame(() => this.#nextFrames(count - 1, callback))
  }
}
